import { FunctionComponent, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import StatusBadge from 'src/shared/statusBadge';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { getJobResult, getJobStatus, reviewJob, updateJobNotes } from 'src/services/api';
import AssignPatientModal from 'src/shared/assignPatientModal';
import type { AnalysisJob, JobResult, SubScanResult, DisplayStatus, PolarData, NpyMetrics, SaccadeEntry } from 'src/types/job';
import { toDisplayStatus, jobIsSubjectLinked } from 'src/types/job';
import Plot from 'react-plotly.js';
import { ReactComponent as DownloadReportIcon } from 'src/icons/download-2.svg';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
`;

const BackButton = styled.button`
    background: none;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
    align-self: flex-start;
    &:hover { background: ${Colors.extra.light_gray}; }
`;

const HeaderCard = styled(Card)`
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const HeaderActions = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const HeaderActionsPrimary = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

const ReviewedInfo = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    margin-top: 2px;
`;

// Sub-scan tabs
const TabBar = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: ${p => p.$active ? 600 : 400};
    cursor: pointer;
    border: 1px solid ${p => p.$active ? Colors.theme.primary : Colors.extra.light_gray};
    background: ${p => p.$active ? `${Colors.theme.primary}12` : Colors.extra.white};
    color: ${p => p.$active ? Colors.theme.primary : Colors.extra.dark_gray};
    transition: all 0.15s ease;
    &:hover { border-color: ${Colors.theme.primary}; }
`;

// Main content grid
const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 16px;
    @media (max-width: 960px) { grid-template-columns: 1fr; }
`;

const MainColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Viewer = styled(Card)`
    padding: 0;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0a0a0a;
    border-radius: 10px;
    overflow: hidden;
`;

const ViewerImage = styled.img`
    max-width: 100%;
    max-height: 600px;
    object-fit: contain;
`;

const ViewerEmpty = styled.div`
    color: #666;
    font-size: 14px;
    text-align: center;
    padding: 40px;
`;

const GifCard = styled(Card)`
    padding: 0;
    overflow: hidden;
    border-radius: 10px;
    background: #0a0a0a;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
`;

const GifImage = styled.img`
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
`;

// Side panel
const SidePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionCard = styled(Card)`
    padding: 16px;
`;

const SectionTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${Colors.extra.dark_gray};
    margin-bottom: 10px;
`;

const MetricRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 5px 0;
    border-bottom: 1px solid ${Colors.extra.light_gray};
    &:last-child { border-bottom: none; }
`;

const MetricName = styled.span`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
`;

const MetricVal = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Colors.extra.black};
    text-align: right;
`;

const FlagBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    background: #FEF3C7;
    color: #92400E;
    margin-right: 6px;
    margin-bottom: 4px;
`;

const MutedLabel = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    font-style: italic;
`;

// Saccade table
const SaccadeTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    tbody tr:nth-child(even) td {
        background: ${Colors.extra.light_gray}40;
    }
`;

const SaccadeTh = styled.th`
    text-align: left;
    padding: 6px 4px;
    border-bottom: 1px solid ${Colors.extra.light_gray};
    color: ${Colors.extra.dark_gray};
    font-weight: 600;
`;

const SaccadeTd = styled.td`
    padding: 4px;
    border-bottom: 1px solid ${Colors.extra.light_gray}44;
    color: ${Colors.extra.black};
`;

// Downloads
const DownloadsToggle = styled.button`
    background: none;
    border: none;
    color: ${Colors.theme.primary};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    &:hover { text-decoration: underline; }
`;

const DownloadGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const DownloadLink = styled.a`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    background: ${Colors.extra.light_gray};
    color: ${Colors.extra.dark_gray};
    text-decoration: none;
    &:hover { background: ${Colors.theme.primary}15; color: ${Colors.theme.primary}; }
`;

// Loading skeleton
const pulse = keyframes`0%,100%{opacity:1;}50%{opacity:0.4;}`;
const SkeletonBlock = styled.div<{ $w?: string; $h?: string }>`
    width: ${p => p.$w || '100%'};
    height: ${p => p.$h || '20px'};
    border-radius: 8px;
    background: ${Colors.extra.light_gray};
    animation: ${pulse} 1.5s ease-in-out infinite;
`;

const ErrorCard = styled(Card)`
    padding: 24px;
    text-align: center;
`;

const EmptyState = styled.div`
    padding: 48px 24px;
    text-align: center;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 10px;
`;

const NotesArea = styled.textarea`
    width: 100%;
    min-height: 80px;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 6px;
    padding: 10px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    outline: none;
    &:focus { border-color: ${Colors.theme.primary}; box-shadow: 0 0 0 3px rgba(37,125,230,0.1); }
`;

const SaveNotesButton = styled.button`
    margin-top: 8px;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid ${Colors.theme.primary};
    background: ${Colors.theme.primary};
    color: #fff;
    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const WarningBanner = styled.div`
    padding: 12px 16px;
    border-radius: 8px;
    background: #FEF3C7;
    border: 1px solid #FDE68A;
    color: #92400E;
    font-size: 13px;
    font-weight: 500;
`;

const MetricUnit = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-left: 4px;
`;

const DownloadReportBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #CBD5E1;
    background: transparent;
    color: #475569;
    text-decoration: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    flex-shrink: 0;
    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
    svg path {
        stroke: currentColor;
    }
    &:hover {
        background: ${Colors.extra.light_gray};
        border-color: #94A3B8;
        color: ${Colors.theme.primary_dark};
    }
`;

const PolarRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    background: rgb(21, 34, 56);
    border-radius: 10px;
    padding: 16px;
    min-height: 350px;
    @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const PolarTitle = styled.div`
    color: rgba(255,255,255,0.85);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 4px;
`;

const METRIC_DEFS: { label: string; unit: string; key: keyof NpyMetrics; isInt?: boolean }[] = [
    { label: 'Microsaccade frequency', unit: 'Hz', key: 'microsaccade_frequency_hz' },
    { label: 'Microsaccade amplitude', unit: 'Deg', key: 'microsaccade_amplitude_deg' },
    { label: 'Microsaccade peak velocity', unit: 'Deg/s', key: 'microsaccade_peak_velocity_degs' },
    { label: 'Drift velocity', unit: 'Deg/s', key: 'drift_velocity_degs' },
    { label: 'Drift ratio', unit: '', key: 'drift_ratio' },
    { label: 'Blink count', unit: '', key: 'blink_count', isInt: true },
    { label: 'Directional deviation', unit: 'Deg', key: 'directional_deviation_deg' },
];

// ---------------------------------------------------------------------------
// Polar Plots Panel (plot_mini_review.py: fixation = thick green line + ref circles;
// microsaccade = open markers, grid, r_max = max(fs rs)*1.3, r ticks step r_max/4)
// ---------------------------------------------------------------------------
const POLAR_BG = 'rgb(21, 34, 56)';
const COLOR_FS_LINE = 'rgb(8, 144, 0)';
const COLOR_MA_MARKER = 'rgb(59, 130, 246)';
const COLOR_REF_GREY = 'rgb(93, 100, 117)';
const SILVER = 'rgba(192, 192, 192, 0.85)';
const FS_R_UPPER = 2.0;
const FS_R_MID = 1.0;
const FS_R_LOWER = 0.5;

function linspaceTheta(n: number): { theta: number[]; rConst: (r: number) => number[] } {
    const theta: number[] = [];
    for (let i = 0; i <= n; i++) theta.push((360 * i) / n);
    const rConst = (r: number) => theta.map(() => r);
    return { theta, rConst };
}

function maxFinite(arr: number[] | undefined): number {
    if (!arr?.length) return 0;
    let m = 0;
    for (const v of arr) {
        if (Number.isFinite(v) && v > m) m = v;
    }
    return m;
}

function microsaccadeRadialMax(fsRsDeg: number[] | undefined, maRsDeg: number[] | undefined): number {
    const fsMax = maxFinite(fsRsDeg);
    const base = fsMax > 0 ? fsMax : (maRsDeg?.length ? maxFinite(maRsDeg) : 0.5);
    return Math.max(base * 1.3, 0.25);
}

/** Ring tick positions (exclude 0.00 per UX). Same step as pipeline: r_max/4. */
function maRadialTicks(rMax: number): number[] {
    const ampInterval = rMax / 4;
    const ticks: number[] = [];
    for (let t = ampInterval; t < rMax - 1e-9; t += ampInterval) {
        ticks.push(Math.round(t * 1e6) / 1e6);
    }
    return ticks;
}

const MA_ANGULAR_TICKS = [0, 45, 90, 135, 180, 225, 270, 315];
const MA_ANGULAR_LABELS = ['0\u00b0', '45\u00b0', '90\u00b0', '135\u00b0', '180\u00b0', '225\u00b0', '270\u00b0', '315\u00b0'];

function makeFSPolarLayout(): Partial<Plotly.Layout> {
    return {
        paper_bgcolor: POLAR_BG,
        plot_bgcolor: POLAR_BG,
        margin: { t: 24, b: 20, l: 36, r: 36 },
        showlegend: false,
        polar: {
            bgcolor: POLAR_BG,
            angularaxis: {
                showticklabels: false,
                showline: false,
                showgrid: false,
            },
            radialaxis: {
                range: [0, FS_R_UPPER] as [number, number],
                autorange: false,
                showticklabels: false,
                showline: false,
                showgrid: false,
            },
        },
    } as Partial<Plotly.Layout>;
}

function makeMAPolarLayout(rMax: number): Partial<Plotly.Layout> {
    const tickvals = maRadialTicks(rMax);
    return {
        paper_bgcolor: POLAR_BG,
        plot_bgcolor: POLAR_BG,
        margin: { t: 28, b: 28, l: 36, r: 36 },
        showlegend: false,
        polar: {
            bgcolor: POLAR_BG,
            angularaxis: {
                direction: 'counterclockwise',
                rotation: 0,
                tickmode: 'array',
                tickvals: MA_ANGULAR_TICKS,
                ticktext: MA_ANGULAR_LABELS,
                showticklabels: true,
                tickfont: { color: SILVER, size: 10 },
                showline: false,
                showgrid: true,
                gridcolor: 'rgba(192, 192, 192, 0.25)',
            },
            radialaxis: {
                range: [0, rMax] as [number, number],
                autorange: false,
                angle: 60, 
                tickangle: 0,
                tickvals,
                ticktext: tickvals.map(v => v.toFixed(2)),
                showticklabels: true,
                tickfont: { color: SILVER, size: 10 },
                showline: false,
                showgrid: true,
                gridcolor: 'rgba(192, 192, 192, 0.3)',
                gridwidth: 1,
            }
        },
    } as Partial<Plotly.Layout>;
}

const HOVER_POLAR = 'Angle: %{theta:.1f}\u00b0<br>Amplitude: %{r:.3f}\u00b0<extra></extra>';

function makeFSPolarTraces(data: PolarData, areaDeg2?: number): any[] {
    const { theta: thetaRing, rConst } = linspaceTheta(100);
    const skipHover = { hoverinfo: 'skip' as const, showlegend: false };
    const ringLabelTrace = {
        type: 'scatterpolar',
        mode: 'text' as const,
        theta: [0, 0, 0],
        r: [FS_R_LOWER, FS_R_MID, FS_R_UPPER],
        text: ['0.50\u00b0', '1.00\u00b0', '2.00\u00b0'],
        textposition: ['middle right', 'middle right', 'middle left'],
        textfont: { color: SILVER, size: 11, family: 'sans-serif' },
        ...skipHover,
    };
    const traces: any[] = [
        { ...skipHover, type: 'scatterpolar', mode: 'lines', theta: thetaRing, r: rConst(FS_R_LOWER), line: { color: COLOR_REF_GREY, width: 1 } },
        { ...skipHover, type: 'scatterpolar', mode: 'lines', theta: thetaRing, r: rConst(FS_R_MID), line: { color: COLOR_REF_GREY, width: 1 } },
        { ...skipHover, type: 'scatterpolar', mode: 'lines', theta: thetaRing, r: rConst(FS_R_UPPER), line: { color: COLOR_REF_GREY, width: 1 } },
        {
            type: 'scatterpolar',
            mode: 'lines',
            theta: data.phis_deg,
            r: data.rs_deg,
            line: { color: COLOR_FS_LINE, width: 5, shape: 'linear' },
            hovertemplate: HOVER_POLAR,
            name: 'Fixation',
        },
        ringLabelTrace,
    ];
    if (typeof areaDeg2 === 'number' && Number.isFinite(areaDeg2)) {
        const rArea = Math.min(FS_R_UPPER * 0.82, FS_R_UPPER - 0.2);
        traces.push({
            type: 'scatterpolar',
            mode: 'text',
            theta: [270],
            r: [rArea],
            text: [`Area: ${areaDeg2.toFixed(2)} deg\u00b2`],
            textposition: 'top center',
            textfont: { color: SILVER, size: 12, family: 'sans-serif' },
            ...skipHover,
        });
    }
    return traces;
}

function makeMAPolarTraces(data: PolarData, rMax: number): any[] {
    const { theta: thetaRing, rConst } = linspaceTheta(100);
    const skipHover = { hoverinfo: 'skip' as const, showlegend: false };
    return [
        { ...skipHover, type: 'scatterpolar', mode: 'lines', theta: thetaRing, r: rConst(rMax), line: { color: COLOR_REF_GREY, width: 1 } },
        {
            type: 'scatterpolar',
            mode: 'markers',
            theta: data.phis_deg,
            r: data.rs_deg,
            marker: {
                symbol: 'circle-open',
                size: 10,
                color: COLOR_MA_MARKER,
                line: { color: COLOR_MA_MARKER, width: 1.5 },
            },
            hovertemplate: HOVER_POLAR,
            name: 'Microsaccade',
        },
    ];
}

const PolarPlotsPanel: FunctionComponent<{ activeScan: SubScanResult; primaryUrl?: string }> = ({
    activeScan,
    primaryUrl,
}) => {
    const fsData = activeScan.fixation_stability_polar;
    const maData = activeScan.microsaccade_amplitude_polar;
    const maRmax = microsaccadeRadialMax(
        fsData && !fsData.error ? fsData.rs_deg : undefined,
        maData && !maData.error ? maData.rs_deg : undefined,
    );

    if ((!fsData || fsData.error) && (!maData || maData.error)) {
        return (
            <Viewer>
                {primaryUrl ? (
                    <ViewerImage src={primaryUrl} alt={`Review - ${activeScan.label}`} />
                ) : (
                    <ViewerEmpty>No visual output for this scan.</ViewerEmpty>
                )}
            </Viewer>
        );
    }

    return (
        <PolarRow>
            <div>
                <PolarTitle>Fixation Stability</PolarTitle>
                {fsData && !fsData.error ? (
                    <Plot
                        data={makeFSPolarTraces(
                            fsData,
                            typeof fsData.area_deg2 === 'number' ? fsData.area_deg2 : undefined,
                        )}
                        layout={{ ...makeFSPolarLayout(), autosize: true } as any}
                        config={{ displayModeBar: false, responsive: true }}
                        useResizeHandler
                        style={{ width: '100%', height: 320 }}
                    />
                ) : (
                    <ViewerEmpty>Fixation data unavailable</ViewerEmpty>
                )}
            </div>
            <div>
                <PolarTitle>Microsaccade Amplitude</PolarTitle>
                {maData && !maData.error ? (
                    <Plot
                        data={makeMAPolarTraces(maData, maRmax)}
                        layout={{ ...makeMAPolarLayout(maRmax), autosize: true } as any}
                        config={{ displayModeBar: false, responsive: true }}
                        useResizeHandler
                        style={{ width: '100%', height: 320 }}
                    />
                ) : (
                    <ViewerEmpty>Microsaccade data unavailable</ViewerEmpty>
                )}
            </div>
        </PolarRow>
    );
};

// ---------------------------------------------------------------------------
// Trace Chart with synchronized playback
// ---------------------------------------------------------------------------
interface TracePoint { time_s: number; h_deg: number; v_deg: number; }

const TraceChartWrap = styled(Card)`
    padding: 16px;
`;

// Layout constants for the trace chart container — kept in JS so the JSX layer
// uses the same numbers as the styled components.
const TRACE_SVG_HEIGHT = 200;       // px, matches TraceSvg's CSS height
const TRACE_Y_LABEL_GUTTER = 36;    // px, container padding-left reserved for the rotated Y-axis label
const TRACE_X_LABEL_GUTTER = 22;    // px, container padding-bottom reserved for the X-axis label

// Wraps the SVG so HTML overlays (axis labels, legend) can be positioned relative to it.
const TraceSvgContainer = styled.div`
    position: relative;
    width: 100%;
    padding-left: ${TRACE_Y_LABEL_GUTTER}px;
    padding-bottom: ${TRACE_X_LABEL_GUTTER}px;
`;

const TraceSvg = styled.svg`
    width: 100%;
    height: ${TRACE_SVG_HEIGHT}px;
    display: block;
`;

// Axis labels rendered as HTML so their font is not distorted by the SVG's
// non-uniform aspect-ratio scaling.
const TRACE_AXIS_FONT_SIZE = '11px';
const TRACE_AXIS_FONT_WEIGHT = 500;
const TRACE_AXIS_FONT_COLOR = '#475569';

// Outer wrapper occupies the gutter on the left of the SVG and uses flex to
// vertically center the rotated label, which avoids the percentage-translate
// pitfalls of CSS rotate.
const YAxisLabelWrap = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: ${TRACE_Y_LABEL_GUTTER}px;
    height: ${TRACE_SVG_HEIGHT}px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
`;

const YAxisLabel = styled.div`
    transform: rotate(-90deg);
    transform-origin: center center;
    white-space: nowrap;
    font-size: ${TRACE_AXIS_FONT_SIZE};
    font-weight: ${TRACE_AXIS_FONT_WEIGHT};
    color: ${TRACE_AXIS_FONT_COLOR};
`;

const XAxisLabel = styled.div`
    position: absolute;
    left: ${TRACE_Y_LABEL_GUTTER}px;
    right: 0;
    bottom: 0;
    height: ${TRACE_X_LABEL_GUTTER}px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${TRACE_AXIS_FONT_SIZE};
    font-weight: ${TRACE_AXIS_FONT_WEIGHT};
    color: ${TRACE_AXIS_FONT_COLOR};
    pointer-events: none;
`;

const TraceLegend = styled.div`
    position: absolute;
    top: 4px;
    right: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 6px;
    pointer-events: none;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${TRACE_AXIS_FONT_SIZE};
    font-weight: ${TRACE_AXIS_FONT_WEIGHT};
    color: ${TRACE_AXIS_FONT_COLOR};
    line-height: 1;
`;

const LegendSwatch = styled.span<{ $color: string }>`
    display: inline-block;
    width: 16px;
    height: 2px;
    background: ${p => p.$color};
    border-radius: 1px;
`;

const PlaybackControls = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
`;

const PlayBtn = styled.button`
    width: 32px; height: 32px;
    border-radius: 50%;
    border: 1px solid ${Colors.theme.primary};
    background: ${Colors.theme.primary};
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { opacity: 0.9; }
`;

const TimeLabel = styled.span`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    min-width: 80px;
`;

const Scrubber = styled.input`
    flex: 1;
    height: 4px;
    accent-color: ${Colors.theme.primary};
    cursor: pointer;
`;

const TraceSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

const SaccadeBandsBtn = styled.button<{ $active: boolean }>`
    flex-shrink: 0;
    padding: 5px 11px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid ${p => (p.$active ? Colors.theme.primary : '#CBD5E1')};
    background: ${p => (p.$active ? `${Colors.theme.primary}12` : 'transparent')};
    color: ${p => (p.$active ? Colors.theme.primary_dark : '#475569')};
    transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
    &:hover {
        border-color: ${Colors.theme.primary};
        color: ${Colors.theme.primary_dark};
    }
`;

const TraceChart: FunctionComponent<{ traceUrl: string; label: string; saccades?: SaccadeEntry[] | null }> = ({
    traceUrl,
    label,
    saccades,
}) => {
    const [data, setData] = useState<TracePoint[]>([]);
    const [traceError, setTraceError] = useState<string | null>(null);
    const [traceLoading, setTraceLoading] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [playTime, setPlayTime] = useState(0);
    const [showSaccadeBands, setShowSaccadeBands] = useState(false);
    const animRef = useRef<number | null>(null);
    const startRef = useRef(0);
    const offsetRef = useRef(0);

    useEffect(() => {
        setShowSaccadeBands(false);
    }, [traceUrl]);

    useEffect(() => {
        setTraceLoading(true);
        setTraceError(null);
        setPlayTime(0);
        setPlaying(false);
        fetch(traceUrl)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
            .then(csv => {
                const lines = csv.trim().split('\n');
                if (lines.length < 2) { setTraceError('Empty trace file'); return; }
                const headers = lines[0].split(',').map(h => h.trim());
                const timeIdx = headers.findIndex(h => h.includes('time'));
                const hIdx = headers.findIndex(h => h.includes('horizontal_motion') && h.includes('deg') && !h.includes('raw'));
                const vIdx = headers.findIndex(h => h.includes('vertical_motion') && h.includes('deg') && !h.includes('raw'));
                if (timeIdx < 0 || hIdx < 0 || vIdx < 0) {
                    setTraceError('Trace data unavailable for this scan');
                    return;
                }
                const pts: TracePoint[] = [];
                for (let i = 1; i < lines.length; i++) {
                    const cols = lines[i].split(',');
                    const t = parseFloat(cols[timeIdx]);
                    const h = parseFloat(cols[hIdx]);
                    const v = parseFloat(cols[vIdx]);
                    if (!isNaN(t) && !isNaN(h) && !isNaN(v)) pts.push({ time_s: t, h_deg: h, v_deg: v });
                }
                setData(pts);
            })
            .catch(e => setTraceError(`Failed to load trace: ${e.message}`))
            .finally(() => setTraceLoading(false));
    }, [traceUrl]);

    const tMin = data.length > 0 ? data[0].time_s : 0;
    const tMax = data.length > 0 ? data[data.length - 1].time_s : 1;
    const duration = tMax - tMin || 1;

    useEffect(() => {
        if (!playing) {
            if (animRef.current) cancelAnimationFrame(animRef.current);
            return;
        }
        startRef.current = performance.now();
        offsetRef.current = playTime;
        const tick = () => {
            const elapsed = (performance.now() - startRef.current) / 1000;
            const t = offsetRef.current + elapsed;
            if (t >= duration) {
                setPlayTime(0);
                setPlaying(false);
                return;
            }
            setPlayTime(t);
            animRef.current = requestAnimationFrame(tick);
        };
        animRef.current = requestAnimationFrame(tick);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [playing, duration]);

    if (traceLoading) return <TraceChartWrap><MutedLabel>Loading trace…</MutedLabel></TraceChartWrap>;
    if (traceError) return <TraceChartWrap><MutedLabel>{traceError}</MutedLabel></TraceChartWrap>;
    if (data.length === 0) return null;

    const W = 800, H = 200, PAD_L = 36, PAD_R = 16, PAD_T = 12, PAD_B = 22;
    let yMin = Infinity, yMax = -Infinity;
    for (const p of data) {
        if (p.h_deg < yMin) yMin = p.h_deg;
        if (p.v_deg < yMin) yMin = p.v_deg;
        if (p.h_deg > yMax) yMax = p.h_deg;
        if (p.v_deg > yMax) yMax = p.v_deg;
    }
    const yRange = yMax - yMin || 1;
    const xScale = (t: number) => PAD_L + ((t - tMin) / (tMax - tMin || 1)) * (W - PAD_L - PAD_R);
    const yScale = (v: number) => H - PAD_B - ((v - yMin) / yRange) * (H - PAD_T - PAD_B);

    const TICK_COUNT = 5;
    const yTicks: number[] = [];
    for (let i = 0; i <= TICK_COUNT; i++) yTicks.push(yMin + (yRange * i) / TICK_COUNT);

    const step = Math.max(1, Math.floor(data.length / 600));
    const sampled = data.filter((_, i) => i % step === 0);

    const hPath = sampled.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.time_s).toFixed(1)},${yScale(p.h_deg).toFixed(1)}`).join(' ');
    const vPath = sampled.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.time_s).toFixed(1)},${yScale(p.v_deg).toFixed(1)}`).join(' ');

    const hasStartedPlayback = playing || playTime > 0;
    const clipWidth = xScale(tMin + playTime) - PAD_L;
    const currentTime = (tMin + playTime).toFixed(2);

    const togglePlay = () => {
        if (playTime >= duration) setPlayTime(0);
        setPlaying(p => !p);
    };

    const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
        const t = parseFloat(e.target.value);
        setPlayTime(t);
        setPlaying(false);
    };

    const clipId = `traceClip-${label.replace(/\s+/g, '')}`;
    const hasSaccadeData = Boolean(saccades && saccades.length > 0);

    return (
        <TraceChartWrap>
            <TraceSectionHeader>
                <SectionTitle style={{ marginBottom: 0 }}>Eye Motion Trace</SectionTitle>
                {hasSaccadeData ? (
                    <SaccadeBandsBtn
                        type="button"
                        $active={showSaccadeBands}
                        aria-pressed={showSaccadeBands}
                        onClick={() => setShowSaccadeBands(v => !v)}
                    >
                        {showSaccadeBands ? 'Hide saccade bands' : 'Show saccade bands'}
                    </SaccadeBandsBtn>
                ) : null}
            </TraceSectionHeader>
            <TraceSvgContainer>
                <YAxisLabelWrap>
                    <YAxisLabel>Amplitude [Deg]</YAxisLabel>
                </YAxisLabelWrap>
                <TraceSvg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                    <defs>
                        <clipPath id={clipId}>
                            <rect x={PAD_L} y={0} width={Math.max(0, clipWidth)} height={H} />
                        </clipPath>
                    </defs>
                    {/* Axes */}
                    <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} stroke="#E5E7EB" strokeWidth="1" />
                    <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} stroke="#E5E7EB" strokeWidth="1" />
                    {/* X-axis ticks: 0, 2, 4, … at least through 10 s, extended if trace is longer */}
                    {(() => {
                        const xTickEnd = Math.max(10, Math.ceil(tMax / 2) * 2);
                        const els: JSX.Element[] = [];
                        for (let ts = 0; ts <= xTickEnd + 1e-9; ts += 2) {
                            const px = xScale(ts);
                            if (px < PAD_L - 0.5 || px > W - PAD_R + 0.5) continue;
                            els.push(
                                <g key={`xt-${ts}`}>
                                    <line x1={px} y1={H - PAD_B} x2={px} y2={H - PAD_B + 4} stroke="#9CA3AF" strokeWidth="1" />
                                    <text x={px} y={H - PAD_B + 14} textAnchor="middle" fontSize="9" fill="#6B7280">{ts}</text>
                                </g>,
                            );
                        }
                        return els;
                    })()}
                    {/* Y-axis ticks */}
                    {yTicks.map((v, i) => {
                        const y = yScale(v);
                        return (
                            <g key={i}>
                                <line x1={PAD_L - 4} y1={y} x2={PAD_L} y2={y} stroke="#9CA3AF" strokeWidth="1" />
                                <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#F3F4F6" strokeWidth="0.5" />
                                <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#6B7280">{v.toFixed(2)}</text>
                            </g>
                        );
                    })}
                    {showSaccadeBands &&
                        (saccades ?? []).map((s, i) => {
                            const t0 = s.start_time_s;
                            const t1 = s.end_time_s;
                            if (t0 == null || t1 == null || !Number.isFinite(t0) || !Number.isFinite(t1)) return null;
                            const a = Math.min(t0, t1);
                            const b = Math.max(t0, t1);
                            let x0 = xScale(a);
                            let x1 = xScale(b);
                            x0 = Math.max(PAD_L, Math.min(x0, W - PAD_R));
                            x1 = Math.max(PAD_L, Math.min(x1, W - PAD_R));
                            const rawW = x1 - x0;
                            const minBandW = 3;
                            const bandW = Math.max(rawW, minBandW);
                            const cx = (x0 + x1) / 2;
                            let rx = cx - bandW / 2;
                            if (rx < PAD_L) rx = PAD_L;
                            if (rx + bandW > W - PAD_R) rx = W - PAD_R - bandW;
                            if (bandW <= 0 || rx + bandW <= PAD_L) return null;
                            return (
                                <rect
                                    key={i}
                                    x={rx}
                                    y={PAD_T}
                                    width={bandW}
                                    height={H - PAD_T - PAD_B}
                                    fill="rgba(245, 158, 11, 0.12)"
                                />
                            );
                        })}
                    {/* Traces: full when idle, clipped during playback */}
                    <g clipPath={hasStartedPlayback ? `url(#${clipId})` : undefined}>
                        <path d={hPath} fill="none" stroke="#2563EB" strokeWidth="1.5" opacity="0.8" />
                        <path d={vPath} fill="none" stroke="#DC2626" strokeWidth="1.5" opacity="0.8" />
                    </g>
                </TraceSvg>
                <TraceLegend>
                    <LegendItem>
                        <LegendSwatch $color="#2563EB" />
                        <span>Horizontal</span>
                    </LegendItem>
                    <LegendItem>
                        <LegendSwatch $color="#DC2626" />
                        <span>Vertical</span>
                    </LegendItem>
                </TraceLegend>
                <XAxisLabel>Time (s)</XAxisLabel>
            </TraceSvgContainer>
            <PlaybackControls>
                <PlayBtn onClick={togglePlay}>{playing ? '||' : '\u25B6'}</PlayBtn>
                <TimeLabel>{currentTime}s / {tMax.toFixed(2)}s</TimeLabel>
                <Scrubber
                    type="range"
                    min={0}
                    max={duration}
                    step={0.01}
                    value={playTime}
                    onChange={handleScrub}
                />
            </PlaybackControls>
        </TraceChartWrap>
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ResultPage: FunctionComponent = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const [result, setResult] = useState<JobResult | null>(null);
    const [job, setJob] = useState<AnalysisJob | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const [downloadsOpen, setDownloadsOpen] = useState(false);
    const [analysisDetailsOpen, setAnalysisDetailsOpen] = useState(true);
    const [notesText, setNotesText] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);

    const assignMode: 'link' | 'reassign' =
        job != null && jobIsSubjectLinked(job) ? 'reassign' : 'link';
    const barePatientLabel = job?.patient_label ?? job?.patient_name ?? null;

    useEffect(() => {
        if (!jobId) return;
        (async () => {
            setLoading(true);
            try {
                const [jobRes, resultRes] = await Promise.all([
                    getJobStatus(jobId),
                    getJobResult(jobId),
                ]);
                setJob(jobRes);
                setNotesText(jobRes?.notes || '');
                setResult(resultRes);
                if (resultRes?.sub_scans?.length > 0) {
                    setActiveKey(resultRes.sub_scans[0].key);
                }
            } catch (e: any) {
                setError(e?.data?.error_message || e?.message || 'Failed to load result');
            } finally {
                setLoading(false);
            }
        })();
    }, [jobId]);

    const handleReview = useCallback(async () => {
        if (!jobId) return;
        try {
            const updated = await reviewJob(jobId, notesText || undefined);
            setJob(updated);
        } catch { /* toast later */ }
    }, [jobId, notesText]);

    const handleSaveNotes = useCallback(async () => {
        if (!jobId) return;
        setNotesSaving(true);
        try {
            const updated = await updateJobNotes(jobId, notesText);
            setJob(updated);
        } catch { /* toast later */ }
        finally { setNotesSaving(false); }
    }, [jobId, notesText]);

    // Loading state
    if (loading) {
        return (
            <DashboardWrapper>
                <Container>
                    <SkeletonBlock $w="120px" $h="36px" />
                    <SkeletonBlock $h="80px" />
                    <SkeletonBlock $h="40px" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
                        <SkeletonBlock $h="400px" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <SkeletonBlock $h="160px" />
                            <SkeletonBlock $h="120px" />
                        </div>
                    </div>
                </Container>
            </DashboardWrapper>
        );
    }

    // Error state
    if (error) {
        return (
            <DashboardWrapper>
                <Container>
                    <BackButton onClick={() => navigate('/scans')}>&larr; Back to Scans</BackButton>
                    <ErrorCard>
                        <Text fontWeight="600" color="#DC2626">Error</Text>
                        <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={8}>{error}</Text>
                    </ErrorCard>
                </Container>
            </DashboardWrapper>
        );
    }

    const display: DisplayStatus = job ? toDisplayStatus(job) : 'Completed';
    const subScans = result?.sub_scans ?? [];
    const activeScan: SubScanResult | undefined = subScans.find(s => s.key === activeKey);
    const flags = result?.quality_flags ?? [];

    // Collect download links for the active sub-scan
    const downloads: { label: string; url: string }[] = [];
    if (activeScan) {
        if (activeScan.annotated_video) downloads.push({ label: 'Annotated Video', url: activeScan.annotated_video.url });
        if (activeScan.raw_video) downloads.push({ label: 'Raw Video', url: activeScan.raw_video.url });
        if (activeScan.trace) downloads.push({ label: 'Trace CSV', url: activeScan.trace.url });
        if (activeScan.stats_summary) downloads.push({ label: 'Stats CSV', url: activeScan.stats_summary.url });
        if (activeScan.saccade_summary) downloads.push({ label: 'Saccade CSV', url: activeScan.saccade_summary.url });
    }
    if (result?.total_summary) downloads.push({ label: 'Total Summary', url: result.total_summary.url });
    if (result?.files) {
        result.files.forEach(f => downloads.push({ label: f.filename, url: f.url }));
    }

    // Primary visual: review screen, fallback to GIF
    const primaryUrl = activeScan?.review_screen?.url ?? activeScan?.gif?.url;

    return (
        <DashboardWrapper>
            <Container>
                <BackButton onClick={() => navigate('/scans')}>&larr; Back to Scans</BackButton>

                {/* Header */}
                <HeaderCard>
                    <HeaderLeft>
                        <Text fontSize={Size.Medium} fontWeight="600" color={Colors.extra.black}>
                            Analysis Result
                        </Text>
                        <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>
                            Scan {jobId?.slice(0, 8)}…
                            {result?.scan_id ? ` · Scan ${result.scan_id.slice(0, 8)}…` : ''}
                            {job?.created_at ? ` · ${new Date(job.created_at).toLocaleDateString()}` : ''}
                        </Text>
                        {job?.reviewed && (
                            <ReviewedInfo>
                                Reviewed by {job.reviewed_by_username ?? 'clinician'}
                                {job.reviewed_at ? ` on ${new Date(job.reviewed_at).toLocaleDateString()}` : ''}
                            </ReviewedInfo>
                        )}
                        {job?.parent_job_id && (
                            <ReviewedInfo>Retry analysis for {String(job.parent_job_id).slice(0, 8)}…</ReviewedInfo>
                        )}
                    </HeaderLeft>
                    <HeaderActions>
                        <HeaderActionsPrimary>
                            <StatusBadge status={display} />
                            {job && job.status === 'SUCCEEDED' && !job.reviewed && (
                                <Button onClick={handleReview}>Mark Reviewed</Button>
                            )}
                            {jobId && job && (
                                <Button
                                    onClick={() => setShowAssignModal(true)}
                                    backgroundColor={assignMode === 'reassign' ? '#9CA3AF' : '#6B7280'}
                                >
                                    {assignMode === 'reassign' ? 'Reassign…' : 'Assign to Patient'}
                                </Button>
                            )}
                        </HeaderActionsPrimary>
                        {result?.report_pdf && (
                            <DownloadReportBtn href={result.report_pdf.url} target="_blank" rel="noopener noreferrer">
                                <DownloadReportIcon aria-hidden />
                                Download Report
                            </DownloadReportBtn>
                        )}
                    </HeaderActions>

                {showAssignModal && jobId && job && (
                    <AssignPatientModal
                        jobIds={[jobId]}
                        mode={assignMode}
                        currentPatientLabel={barePatientLabel}
                        currentPatientIsSession={job.patient_is_session}
                        onClose={() => setShowAssignModal(false)}
                        onAssigned={() => { if (jobId) getJobStatus(jobId).then(setJob); }}
                    />
                )}
                </HeaderCard>

                {/* Incomplete output warning */}
                {flags.includes('INCOMPLETE_OUTPUT') && (
                    <WarningBanner>
                        Warning: Output may be incomplete — some files could be missing.
                    </WarningBanner>
                )}

                {/* Empty state */}
                {subScans.length === 0 && (
                    <EmptyState>
                        <Text fontSize={Size.Medium} fontWeight="500" color={Colors.extra.dark_gray}>
                            No results found
                        </Text>
                        <Text fontSize={Size.Small} color={Colors.extra.dark_gray} marginTop={8}>
                            The pipeline may not have produced output for this session,
                            or results are stored at a different location.
                        </Text>
                    </EmptyState>
                )}

                {/* Sub-scan selector */}
                {subScans.length > 0 && (
                    <>
                        <TabBar>
                            {subScans.map(ss => {
                                const modeTooltip: Record<number, string> = {
                                    1: 'Fixation Stability',
                                    2: 'Montage',
                                    3: 'Saccade Tracking',
                                };
                                return (
                                    <Tab
                                        key={ss.key}
                                        $active={activeKey === ss.key}
                                        onClick={() => { setActiveKey(ss.key); setDownloadsOpen(false); }}
                                        title={modeTooltip[ss.mode] ?? `Mode ${ss.mode}`}
                                    >
                                        {ss.label}
                                    </Tab>
                                );
                            })}
                        </TabBar>

                        {activeScan && (
                            <ContentGrid>
                                <MainColumn>
                                    {/* Polar plots (primary) — falls back to review screen PNG */}
                                    <PolarPlotsPanel activeScan={activeScan} primaryUrl={primaryUrl} />

                                    {/* GIF */}
                                    {activeScan.gif && (
                                        <GifCard>
                                            <GifImage src={activeScan.gif.url} alt={`GIF - ${activeScan.label}`} />
                                        </GifCard>
                                    )}

                                    {/* Trace playback */}
                                    {activeScan.trace && (
                                        <TraceChart
                                            traceUrl={activeScan.trace.url}
                                            label={activeScan.label}
                                            saccades={activeScan.saccade_summary?.data ?? undefined}
                                        />
                                    )}

                                    {/* Downloads (collapsed) */}
                                    {downloads.length > 0 && (
                                        <div>
                                            <DownloadsToggle onClick={() => setDownloadsOpen(!downloadsOpen)}>
                                                {downloadsOpen ? '▾ Hide downloads' : '▸ Downloads & raw files'}
                                                {` (${downloads.length})`}
                                            </DownloadsToggle>
                                            {downloadsOpen && (
                                                <DownloadGrid>
                                                    {downloads.map((d, i) => (
                                                        <DownloadLink key={i} href={d.url} target="_blank" rel="noopener noreferrer">
                                                            {d.label}
                                                        </DownloadLink>
                                                    ))}
                                                </DownloadGrid>
                                            )}
                                        </div>
                                    )}
                                </MainColumn>

                                <SidePanel>
                                    {/* Key Metrics (prefer npy_metrics, fallback to stats_summary) */}
                                    <SectionCard>
                                        <SectionTitle>Key Metrics</SectionTitle>
                                        {activeScan.npy_metrics && !activeScan.npy_metrics.error ? (
                                            METRIC_DEFS.map(m => {
                                                const raw = activeScan.npy_metrics![m.key as keyof NpyMetrics];
                                                if (typeof raw !== 'number') return null;
                                                const display = m.isInt ? Math.round(raw) : raw.toFixed(2);
                                                return (
                                                    <MetricRow key={m.key}>
                                                        <MetricName>{m.label}</MetricName>
                                                        <MetricVal>
                                                            {display}{m.unit && <MetricUnit>{m.unit}</MetricUnit>}
                                                        </MetricVal>
                                                    </MetricRow>
                                                );
                                            })
                                        ) : activeScan.stats_summary?.data ? (
                                            Object.entries(activeScan.stats_summary.data).map(([k, v]) => (
                                                <MetricRow key={k}>
                                                    <MetricName>{k.replace(/_/g, ' ')}</MetricName>
                                                    <MetricVal>
                                                        {typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : String(v ?? '\u2014')}
                                                    </MetricVal>
                                                </MetricRow>
                                            ))
                                        ) : (
                                            <MutedLabel>
                                                Metrics unavailable
                                                {activeScan.npy_metrics?.error ? `: ${activeScan.npy_metrics.error}` : ''}
                                                {activeScan.stats_summary?.error ? `: ${activeScan.stats_summary.error}` : ''}
                                            </MutedLabel>
                                        )}
                                    </SectionCard>

                                    {/* Quality flags */}
                                    {flags.length > 0 && (
                                        <SectionCard>
                                            <SectionTitle>Quality Flags</SectionTitle>
                                            <div>
                                                {flags.map(f => (
                                                    <FlagBadge key={f}>{f.replace(/_/g, ' ')}</FlagBadge>
                                                ))}
                                            </div>
                                        </SectionCard>
                                    )}

                                    {/* Saccade table */}
                                    {activeScan.saccade_summary && (
                                        <SectionCard>
                                            <SectionTitle>Saccades</SectionTitle>
                                            {activeScan.saccade_summary.data && activeScan.saccade_summary.data.length > 0 ? (
                                                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                                                    <SaccadeTable>
                                                        <thead>
                                                            <tr>
                                                                <SaccadeTh>#</SaccadeTh>
                                                                <SaccadeTh>Time (s)</SaccadeTh>
                                                                <SaccadeTh>Duration (s)</SaccadeTh>
                                                                <SaccadeTh>Speed (deg/s)</SaccadeTh>
                                                                <SaccadeTh>Amplitude (deg)</SaccadeTh>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {activeScan.saccade_summary.data.map((s, i) => {
                                                                const dur =
                                                                    s.start_time_s != null && s.end_time_s != null
                                                                        ? Math.max(0, s.end_time_s - s.start_time_s)
                                                                        : null;
                                                                return (
                                                                    <tr key={i}>
                                                                        <SaccadeTd>{s.index}</SaccadeTd>
                                                                        <SaccadeTd>{s.start_time_s != null ? s.start_time_s.toFixed(2) : '\u2014'}</SaccadeTd>
                                                                        <SaccadeTd>{dur != null ? dur.toFixed(3) : '\u2014'}</SaccadeTd>
                                                                        <SaccadeTd>{s.speed_degs != null ? s.speed_degs.toFixed(1) : '\u2014'}</SaccadeTd>
                                                                        <SaccadeTd>{s.amp_deg != null ? s.amp_deg.toFixed(2) : '\u2014'}</SaccadeTd>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </SaccadeTable>
                                                </div>
                                            ) : (
                                                <MutedLabel>
                                                    Saccade data unavailable{activeScan.saccade_summary.error ? `: ${activeScan.saccade_summary.error}` : ''}
                                                </MutedLabel>
                                            )}
                                        </SectionCard>
                                    )}

                                    {/* Job details — collapsed by default */}
                                    <SectionCard>
                                        <DownloadsToggle
                                            type="button"
                                            onClick={() => setAnalysisDetailsOpen(o => !o)}
                                        >
                                            {analysisDetailsOpen ? '▾ Analysis Details' : '▸ Analysis Details'}
                                        </DownloadsToggle>
                                        {analysisDetailsOpen && (
                                            <>
                                                <MetricRow>
                                                    <MetricName>Pipeline</MetricName>
                                                    <MetricVal>{job?.pipeline_version ?? '—'}</MetricVal>
                                                </MetricRow>
                                                <MetricRow>
                                                    <MetricName>Model</MetricName>
                                                    <MetricVal>{job?.model_version ?? '—'}</MetricVal>
                                                </MetricRow>
                                                <MetricRow>
                                                    <MetricName>Initiated by</MetricName>
                                                    <MetricVal>{job?.initiated_by_username ?? '—'}</MetricVal>
                                                </MetricRow>
                                                {job?.started_at && (
                                                    <MetricRow>
                                                        <MetricName>Started</MetricName>
                                                        <MetricVal>{new Date(job.started_at).toLocaleString()}</MetricVal>
                                                    </MetricRow>
                                                )}
                                                {job?.completed_at && (
                                                    <MetricRow>
                                                        <MetricName>Completed</MetricName>
                                                        <MetricVal>{new Date(job.completed_at).toLocaleString()}</MetricVal>
                                                    </MetricRow>
                                                )}
                                            </>
                                        )}
                                    </SectionCard>

                                    {/* Clinician notes */}
                                    <SectionCard>
                                        <SectionTitle>Notes</SectionTitle>
                                        <NotesArea
                                            placeholder="Add clinical notes about this result…"
                                            value={notesText}
                                            onChange={e => setNotesText(e.target.value)}
                                        />
                                        {notesText !== (job?.notes || '') && (
                                            <SaveNotesButton onClick={handleSaveNotes} disabled={notesSaving}>
                                                {notesSaving ? 'Saving…' : 'Save Notes'}
                                            </SaveNotesButton>
                                        )}
                                    </SectionCard>
                                </SidePanel>
                            </ContentGrid>
                        )}
                    </>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default ResultPage;
